/**
 * Market Data Hooks
 * React Query hooks for fetching and managing market data
 */

'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { appApi } from '@/domains/market-data/api';
import { queryKeys, staleTimeConfig } from '@/shared/providers/react-query/client';

// =============================================================================
// MARKET DATA HOOKS
// =============================================================================

/**
 * Get ticker data for a single symbol
 */
export const useTicker = (symbol: string) => {
  return useQuery({
    queryKey: queryKeys.marketData.ticker(symbol),
    queryFn: async () => {
      const response = await appApi.marketData.getTicker(symbol);
      return response.data;
    },
    staleTime: staleTimeConfig.realTime,
    refetchInterval: 5000, // Refetch every 5 seconds
  });
};

/**
 * Get OHLCV data for charting
 */
function resolveOhlcvWindow(timeframe: string, limit: number) {
  if (timeframe === '1y' || timeframe === '1Y') {
    return { timeframe: '1d' as const, limit: Math.max(limit, 365) }
  }
  return { timeframe, limit }
}

export const useOHLCV = (symbol: string, timeframe: string = '1h', limit: number = 100) => {
  const window = resolveOhlcvWindow(timeframe, limit)
  return useQuery({
    queryKey: queryKeys.marketData.ohlcv.data(symbol, timeframe, { limit: window.limit }),
    queryFn: async () => {
      const response = await appApi.marketData.getOHLCV({
        symbol,
        timeframe: window.timeframe as "1h" | "1m" | "5m" | "15m" | "30m" | "4h" | "1d" | "1w" | "1M",
        limit: window.limit
      });
      return response.data;
    },
    staleTime: staleTimeConfig.marketData,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

/**
 * Get order book data
 */
export const useOrderBook = (symbol: string, depth: number = 20) => {
  return useQuery({
    queryKey: queryKeys.marketData.orderBook(symbol, depth),
    queryFn: async () => {
      const response = await appApi.marketData.getOrderBook(symbol, depth);
      return response.data;
    },
    staleTime: staleTimeConfig.realTime,
    refetchInterval: 2000, // Refetch every 2 seconds
  });
};

/**
 * Get ticker data for multiple symbols
 */
export const useTickerList = (symbols: string[]) => {
  return useQuery({
    queryKey: queryKeys.marketData.tickerList(symbols),
    queryFn: async () => {
      if (symbols.length === 0) return [];
      const response = await appApi.marketData.getMultipleTickers(symbols);
      return response.data ?? [];
    },
    enabled: symbols.length > 0,
    staleTime: staleTimeConfig.realTime,
    refetchInterval: 5000,
  });
};

/**
 * Search tradeable names the market-data backend currently lists.
 */
export function useListedSymbolSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: queryKeys.marketData.symbolSearch(q),
    queryFn: async () => {
      const response = await appApi.marketData.searchListedSymbols(q);
      return response.data;
    },
    enabled: q.length >= 2,
    staleTime: staleTimeConfig.marketData,
  });
}

/**
 * Hook to fetch real-time quote data for a symbol
 */
export function useQuote(symbol: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.marketData.quotes.current(symbol),
    queryFn: async () => {
      const response = await appApi.marketData.getTicker(symbol);
      return response.data;
    },
    staleTime: staleTimeConfig.realTime,
    refetchInterval: 1000, // Refetch every second for real-time updates
  });
}

/**
 * Hook to fetch technical indicators
 */
export function useTechnicalIndicators(
  symbol: string,
  indicator: string,
  params?: Record<string, any>
) {
  return useQuery({
    queryKey: queryKeys.marketData.indicators.data(symbol, indicator, params),
    queryFn: async () => {
      const response = await appApi.marketData.getTechnicalIndicators(symbol, [indicator]);
      return response.data;
    },
    staleTime: staleTimeConfig.marketData,
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Hook to fetch batch quotes for multiple symbols
 */
export function useBatchQuotes(symbols: string[]) {
  return useQuery({
    queryKey: queryKeys.marketData.quotes.batch(symbols),
    queryFn: async () => {
      const promises = symbols.map(symbol =>
        appApi.marketData.getTicker(symbol).then(response => response.data)
      );
      return Promise.all(promises);
    },
    staleTime: staleTimeConfig.realTime,
    refetchInterval: 5000, // Refetch every 5 seconds
  });
}

/**
 * Hook to fetch historical data
 */
export function useHistoricalData(
  symbol: string,
  timeframe: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['market-data', 'historical', symbol, timeframe, startDate, endDate],
    queryFn: async () => {
      const response = await appApi.marketData.getHistoricalData(symbol);
      return response.data;
    },
    staleTime: staleTimeConfig.marketData,
    enabled: !!symbol && !!timeframe,
  });
}

/**
 * Hook to manage watchlist with real-time updates
 */
export function useWatchlist(symbols: string[]) {
  const [watchlistData, setWatchlistData] = useState<Map<string, any>>(new Map());

  // Fetch initial data
  const { data: initialData, isLoading, error } = useBatchQuotes(symbols);

  // Update watchlist data when new data arrives
  useEffect(() => {
    if (initialData) {
      const newData = new Map();
      initialData.forEach((quote: any) => {
        newData.set(quote.symbol, quote);
      });
      setWatchlistData(newData);
    }
  }, [initialData]);

  // Add symbol to watchlist
  const addSymbol = useCallback((symbol: string) => {
    // Implementation would depend on your watchlist management
    console.log('Add symbol to watchlist:', symbol);
  }, []);

  // Remove symbol from watchlist
  const removeSymbol = useCallback((symbol: string) => {
    // Implementation would depend on your watchlist management
    console.log('Remove symbol from watchlist:', symbol);
  }, []);

  return {
    data: Array.from(watchlistData.values()),
    isLoading,
    error,
    addSymbol,
    removeSymbol,
  };
}

/**
 * Hook for real-time price tracking with change calculation
 */
export function usePriceTracker(symbol: string) {
  const [priceHistory, setPriceHistory] = useState<number[]>([]);

  const { data: quote, isLoading, error } = useQuote(symbol);

  // Track price changes
  useEffect(() => {
    if (quote?.price) {
      setPriceHistory(prev => {
        const newHistory = [...prev, quote.price];
        // Keep only last 100 prices for memory efficiency
        return newHistory.slice(-100);
      });
    }
  }, [quote?.price]);

  // Calculate price change and trend
  const lastPrice = priceHistory.at(-1);
  const prevPrice = priceHistory.at(-2);
  const priceChange = lastPrice !== undefined && prevPrice !== undefined
    ? lastPrice - prevPrice
    : 0;

  const trend = priceChange > 0 ? 'up' : priceChange < 0 ? 'down' : 'neutral';

  return {
    currentPrice: quote?.price || 0,
    priceChange,
    trend,
    priceHistory,
    isLoading,
    error,
  };
}

/**
 * Hook for chart data with technical analysis
 */
export function useChartData(
  symbol: string,
  timeframe: string = '1h',
  indicators: string[] = []
) {
  // Fetch OHLCV data
  const { data: ohlcvData, isLoading: ohlcvLoading, error: ohlcvError } = useOHLCV(
    symbol,
    timeframe
  );

  // Fetch technical indicators - hooks must be called unconditionally
  const macdQuery = useTechnicalIndicators(symbol, 'MACD');
  const rsiQuery = useTechnicalIndicators(symbol, 'RSI');
  const smaQuery = useTechnicalIndicators(symbol, 'SMA');

  const indicatorQueries = [
    indicators.includes('MACD') ? macdQuery : null,
    indicators.includes('RSI') ? rsiQuery : null,
    indicators.includes('SMA') ? smaQuery : null,
  ].filter((query): query is NonNullable<typeof query> => query !== null);

  const indicatorsLoading = indicatorQueries.some(query => query.isLoading);
  const indicatorsError = indicatorQueries.find(query => query.error)?.error;

  // Combine data
  const chartData = {
    ohlcv: ohlcvData || [],
    indicators: indicatorQueries.reduce((acc, query, index) => {
      const key = indicators[index];
      if (key && query.data) {
        acc[key] = query.data;
      }
      return acc;
    }, {} as Record<string, any>),
  };

  return {
    data: chartData,
    isLoading: ohlcvLoading || indicatorsLoading,
    error: ohlcvError || indicatorsError,
  };
}

/**
 * Hook to fetch available data sources for market data
 */
export function useDataSources() {
  return useQuery({
    queryKey: ['market-data', 'data-sources'],
    queryFn: async () => {
      const response = await appApi.marketData.getDataSources();
      return response.data;
    },
    staleTime: staleTimeConfig.symbols, // Use same stale time as symbols
  });
}

/**
 * Export all hooks as a namespace for convenient usage
 */
export const useMarketData = {
  useTicker,
  useOHLCV,
  useOrderBook,
  useTickerList,
  useListedSymbolSearch,
  useQuote,
  useTechnicalIndicators,
  useBatchQuotes,
  useHistoricalData,
  useWatchlist,
  usePriceTracker,
  useChartData,
};
